import { useState } from "react";
import { toast } from "sonner";
import { PageHero } from "@/components/site/Sections";
import { Field, SelectField, TextArea } from "@/components/site/FormControls";
import { submitBuyer } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const INV_TYPE = ["Fix & Flip Investor", "Buy & Hold Investor", "BRRRR Investor", "Landlord", "Multifamily Investor", "Wholesaler", "Other"];
const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily 5+", "Condo", "Townhouse", "Mixed Use", "Commercial", "Land", "Other"];
const STRATEGY = ["Fix & Flip", "Buy & Hold", "BRRRR", "Long-Term Rental", "Short-Term Rental", "Multifamily", "Other"];
const REHAB = ["Turnkey / No Rehab", "Light Cosmetic", "Medium Rehab", "Heavy Rehab", "Full Gut"];
const FINANCING = ["Cash", "Hard Money", "DSCR", "Fix & Flip Financing", "Bridge Financing", "Conventional", "Not Sure"];
const MAYBE = ["Yes", "No", "Maybe"];

const initial = {
  first_name: "", last_name: "", phone: "", email: "", investor_type: "",
  states_text: "", counties_text: "", property_type: "", max_purchase_price: "",
  rehab_level: "", primary_strategy: "", financing_type: "", wants_financing: "Maybe", notes: "",
};
const toArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function RegisterBuyBox() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.email || !form.max_purchase_price) {
      toast.error("Please complete name, contact and maximum purchase price.");
      return;
    }
    setLoading(true);
    try {
      await submitBuyer({
        ...form,
        states: toArr(form.states_text),
        counties: toArr(form.counties_text),
        property_types: form.property_type ? [form.property_type] : [],
        rehab_levels: form.rehab_level ? [form.rehab_level] : [],
        source: "quick-buybox",
      });
      setDone(true);
      toast.success("Buy Box registered.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Registered" title="Buy Box Registered." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Your acquisition criteria have been saved. We'll match you with opportunities that fit. Property availability is not guaranteed.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="Register Your Buy Box"
        title="Register Your Buy Box."
        subtitle="A few basic questions so we can match you with the right properties. Want the full version? Use I Want to Buy for the complete Buy Box."
      />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <form data-testid="register-buybox-form" onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <h3 className="font-mono-x text-xs uppercase tracking-widest text-gold">Your Details</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" name="first_name" value={form.first_name} onChange={set} required half testid="bb-first-name" />
              <Field label="Last Name" name="last_name" value={form.last_name} onChange={set} required half testid="bb-last-name" />
              <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={set} required half testid="bb-phone" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={set} required half testid="bb-email" />
              <SelectField label="Investor Type" name="investor_type" value={form.investor_type} onChange={set} options={INV_TYPE} half testid="bb-investor-type" />
            </div>

            <h3 className="mt-8 font-mono-x text-xs uppercase tracking-widest text-gold">What You're Looking For</h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label="Target States (comma separated)" name="states_text" value={form.states_text} onChange={set} half testid="bb-states" />
              <Field label="Target Counties / Cities (comma separated)" name="counties_text" value={form.counties_text} onChange={set} half testid="bb-counties" />
              <SelectField label="Property Type" name="property_type" value={form.property_type} onChange={set} options={PROP_TYPES} half testid="bb-prop-type" />
              <Field label="Maximum Purchase Price" name="max_purchase_price" value={form.max_purchase_price} onChange={set} required half testid="bb-max-price" />
              <SelectField label="Rehab Tolerance" name="rehab_level" value={form.rehab_level} onChange={set} options={REHAB} half testid="bb-rehab" />
              <SelectField label="Primary Strategy" name="primary_strategy" value={form.primary_strategy} onChange={set} options={STRATEGY} half testid="bb-strategy" />
              <SelectField label="Financing Method" name="financing_type" value={form.financing_type} onChange={set} options={FINANCING} half testid="bb-financing" />
              <SelectField label="Want Financing Help?" name="wants_financing" value={form.wants_financing} onChange={set} options={MAYBE} half testid="bb-wants-financing" />
              <TextArea label="Notes" name="notes" value={form.notes} onChange={set} placeholder="Anything else about your Buy Box…" testid="bb-notes" />
            </div>

            <button type="submit" disabled={loading} data-testid="register-buybox-submit-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold text-[#0B132B] transition-all hover:bg-[#E5C158] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{loading ? "Registering…" : "Register My Buy Box"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
